'use client';

// Colors
import type { MantineThemeOverride } from '@mantine/core';
import colors from '@ui-kit/colors';

// Fonts
import { FONT_WEIGHT, fontFamily } from '@ui-kit/fonts';

export const breakpoints = {
  /** 500px */
  xs: '31.25rem',

  /** 800px */
  sm: '50rem',

  /** 1000px */
  md: '62.5rem',

  /** 1200px */
  lg: '75rem',

  /** 1400px */
  xl: '87.5rem',
} as const;

export const isBreakpoint = (
  bp: string | number,
): bp is keyof typeof breakpoints =>
  typeof bp === 'string' && ['xs', 'sm', 'md', 'lg', 'xl'].includes(bp);

export const spacing = {
  /** 15px */
  xs: '0.938rem',

  /** 20px */
  sm: '1.25rem',

  /** 25px */
  md: '1.563rem',

  /** 30px */
  lg: '1.875rem',

  /** 40px */
  xl: '2.5rem',
} as const;

export const fontSizes = {
  /** 10px */
  xs: '0.625rem',

  /** 13px */
  sm: '0.813rem',

  /** 14px */
  md: '0.875rem',

  /** 16px */
  lg: '1rem',

  /** 20px */
  xl: '1.25rem',
} as const;

export const headingSizes = {
  h1: {
    fontWeight: FONT_WEIGHT.bolder,
    /** 100px */ fontSize: '6.25rem',
  },
  h2: {
    fontWeight: FONT_WEIGHT.bolder,
    /** 73px */ fontSize: '4.563rem',
  },
  h3: {
    fontWeight: FONT_WEIGHT.normal,
    /** 52px */ fontSize: '3.25rem',
  },
  h4: {
    fontWeight: FONT_WEIGHT.normal,
    /** 38px */ fontSize: '2.375rem',
  },
  h5: {
    fontWeight: FONT_WEIGHT.normal,
    /** 28px */ fontSize: '1.75rem',
  },
  h6: {
    fontWeight: FONT_WEIGHT.normal,
    /** 24px */ fontSize: '1.5rem',
  },
} as const;

export const radius = {
  /** 6px */
  xs: '0.375rem',

  /** 10px */
  sm: '0.625rem',

  /** 14px */
  md: '0.875rem',

  /** 18px */
  lg: '1.125rem',

  /** 24px */
  xl: '1.5rem',
};

/**
 * Two-layer elevation shadows (close + ambient) for clear depth hierarchy.
 * Use these via the Mantine `shadow` prop or vars in *.css.ts.
 */
export const shadows = {
  /** Subtle hairline, used on chips/buttons at rest */
  xs: '0 1px 2px rgba(34, 38, 41, 0.04), 0 1px 3px rgba(34, 38, 41, 0.06)',

  /** Buttons hover, chips raised, small popovers */
  sm: '0 2px 8px rgba(34, 38, 41, 0.06), 0 1px 2px rgba(34, 38, 41, 0.04)',

  /** Cards, panels, default tables */
  md: '0 8px 20px rgba(34, 38, 41, 0.07), 0 2px 4px rgba(34, 38, 41, 0.04)',

  /** Modals, dropdowns, command palette, AI input dock */
  lg: '0 16px 40px rgba(34, 38, 41, 0.10), 0 4px 8px rgba(34, 38, 41, 0.06)',

  /** Drawers, full-screen overlays */
  xl: '0 24px 60px rgba(34, 38, 41, 0.14), 0 8px 16px rgba(34, 38, 41, 0.08)',
};

/**
 * Motion language — durations and easings used across the app.
 * Available via theme.other.motion in components or vars.other.motion in *.css.ts.
 */
export const motion = {
  duration: {
    /** 80ms — tab switch, toggle */
    instant: '80ms',
    /** 150ms — hover, focus, color */
    fast: '150ms',
    /** 220ms — panel slide, dropdown */
    base: '220ms',
    /** 320ms — drawer, page transition */
    slow: '320ms',
  },
  easing: {
    /** Default smooth ease — most UI motion */
    standard: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    /** Element entering view */
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    /** Element leaving view */
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    /** Emphasis / attention */
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

const base = {
  /* -------------------------------------------------------------------------- */
  /*                                 Dimensions                                 */
  /* -------------------------------------------------------------------------- */

  radius,

  breakpoints,

  spacing,

  shadows,

  /* -------------------------------------------------------------------------- */
  /*                             Colors (invariant)                             */
  /* -------------------------------------------------------------------------- */

  colors,

  /* -------------------------------------------------------------------------- */
  /*                                 Typography                                 */
  /* -------------------------------------------------------------------------- */

  // Shared font-sizes for non-heading items
  fontFamily,
  fontSizes,
  headings: { sizes: headingSizes },

  /* -------------------------------------------------------------------------- */
  /*                          Custom tokens (other slot)                        */
  /* -------------------------------------------------------------------------- */

  other: {
    motion,
  },

  /* -------------------------------------------------------------------------- */
  /*                              Focus & Default UX                            */
  /* -------------------------------------------------------------------------- */

  focusRing: 'auto',
  cursorType: 'pointer',
} satisfies MantineThemeOverride;

export default base;
